import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { SaveStatus } from "../../../shared/utility/save-status";
import { KModal } from "../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../ui-kit/k-modal/k-modal-footer";
import KuikaButton, {
  type IKuikaButtonStatusLabels,
  type KuikaButtonStatus
} from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaH5 from "../../ui-kit/typography/H5/H5";
import KuikaTextMedium from "../../ui-kit/typography/TextMedium/TextMedium";
import "./new-board-modal.scss";
import { CustomFieldDef, CustomFieldType, ISSUE_TYPES, ISSUE_TYPE_LABELS, IssueType } from "./types";

interface CustomFieldModalProps {
  open: boolean;
  // When present, the modal edits this field instead of creating one (the type is locked, since
  // changing it would reinterpret values already stored on issues).
  existing?: CustomFieldDef;
  onClose: () => void;
  onSubmit: (name: string, type: CustomFieldType, options: string[], issueTypes: IssueType[]) => void;
}

interface TypeOption {
  value: CustomFieldType;
  label: string;
}

/** A new field is added rather than saved; an edit keeps the default "Saved". */
const ADD_STATUS_LABELS: IKuikaButtonStatusLabels = { success: "Added" };

const TYPE_OPTIONS: TypeOption[] = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "select", label: "Select (dropdown)" },
  { value: "checkbox", label: "Checkbox" },
  { value: "user", label: "User" }
];

// Create or edit a custom field: name, a real type picker (and options for select), plus an
// "Applies to" issue-type scope (empty = every type, JIRA-style). The type chain replaces the old
// window.prompt flow; on edit the type is fixed so stored values keep their meaning.
const CustomFieldModal: React.FC<CustomFieldModalProps> = ({ open, existing, onClose, onSubmit }) => {
  const [name, setName] = useState<string>("");
  const [type, setType] = useState<CustomFieldType>("text");
  const [optionsText, setOptionsText] = useState<string>("");
  const [issueTypes, setIssueTypes] = useState<IssueType[]>([]);
  // The field goes into the project at once (the store is optimistic), so the commit has no loading
  // phase — only the ✓, held long enough to be read before the dialog leaves.
  const [submitStatus, setSubmitStatus] = useState<KuikaButtonStatus>("idle");
  const submitMachineRef = useRef<SaveStatus | null>(null);
  if (submitMachineRef.current === null) {
    submitMachineRef.current = new SaveStatus(setSubmitStatus);
  }
  const submitMachine: SaveStatus = submitMachineRef.current;
  useEffect((): (() => void) => (): void => submitMachine.dispose(), [submitMachine]);

  useEffect((): void => {
    if (!open) return;
    setName(existing?.name ?? "");
    setType(existing?.type ?? "text");
    setOptionsText(existing?.options.join(", ") ?? "");
    setIssueTypes(existing?.issueTypes ?? []);
  }, [open, existing]);

  const isEdit: boolean = Boolean(existing);

  const options: string[] = optionsText
    .split(",")
    .map((entry: string) => entry.trim())
    .filter((entry: string) => entry.length > 0);

  const canSubmit: boolean = Boolean(name.trim()) && (type !== "select" || options.length > 0);

  const toggleIssueType = (entry: IssueType): void => {
    setIssueTypes((prev: IssueType[]) =>
      prev.includes(entry) ? prev.filter((value: IssueType) => value !== entry) : [...prev, entry]
    );
  };

  const handleSubmit = (): void => {
    if (!canSubmit) return;
    // A second press during the ✓ would add the same field twice.
    if (!submitMachine.canStart) return;
    // Keep issue types in the canonical display order so the stored set is stable regardless of
    // click order.
    const ordered: IssueType[] = ISSUE_TYPES.filter((entry: IssueType) => issueTypes.includes(entry));
    onSubmit(name.trim(), type, type === "select" ? options : [], ordered);
    submitMachine.succeed(onClose);
  };

  // Cancel, the X and Escape. During the ✓ hold the dialog is already on its way out; a close then
  // would let the pending one shut a dialog reopened in the meantime.
  const handleClose = (): void => {
    if (submitMachine.current === "success") return;
    onClose();
  };

  return (
    <KModal
      showModal={open}
      closeModal={handleClose}
      centered
      width={420}
      header={<KuikaH5>{isEdit ? "Edit custom field" : "New custom field"}</KuikaH5>}
      footer={
        <KModalFooter
          onCancel={handleClose}
          submitLabel={isEdit ? "Save" : "Add field"}
          onSubmit={handleSubmit}
          disabled={!canSubmit}
          status={submitStatus}
          statusLabels={isEdit ? undefined : ADD_STATUS_LABELS}
        />
      }
    >
      <div className="kuika-new-board-modal">
        <div className="kuika-new-board-modal__field">
          <KuikaTextMedium>Name</KuikaTextMedium>
          <KuikaInput
            value={name}
            autoFocus
            onChange={(_event, value: string | number) => setName(String(value))}
            placeholder="e.g. Team, Severity, Target date"
          />
        </div>

        <div className="kuika-new-board-modal__field">
          <KuikaTextMedium>Type</KuikaTextMedium>
          <KuikaSelectbox
            style={{ width: "100%" }}
            value={type}
            disabled={isEdit}
            onChange={(value: string | number | null) => setType((value as CustomFieldType) ?? "text")}
            options={TYPE_OPTIONS}
          />
        </div>

        {type === "select" && (
          <div className="kuika-new-board-modal__field">
            <KuikaTextMedium>Options</KuikaTextMedium>
            <KuikaInput
              value={optionsText}
              onChange={(_event, value: string | number) => setOptionsText(String(value))}
              placeholder="Comma separated, e.g. Low, Medium, High"
            />
          </div>
        )}

        <div className="kuika-new-board-modal__field">
          <KuikaTextMedium>Applies to</KuikaTextMedium>
          <div className="kuika-new-board-modal__chips">
            {ISSUE_TYPES.map((entry: IssueType) => (
              <button
                key={entry}
                type="button"
                className={classNames("kuika-new-board-modal__chip", {
                  "kuika-new-board-modal__chip--on": issueTypes.includes(entry)
                })}
                onClick={() => toggleIssueType(entry)}
              >
                {ISSUE_TYPE_LABELS[entry]}
              </button>
            ))}
          </div>
          <div className="kuika-new-board-modal__hint">
            {issueTypes.length === 0
              ? "Applies to every issue type. Pick types to scope it."
              : "Only shows on the selected types."}
          </div>
        </div>
      </div>
    </KModal>
  );
};

export default CustomFieldModal;
